import React, { useState, useEffect, useRef } from "react";
import {
  FaUser,
  FaFile,
  FaComment,
  FaUniversity,
  FaCheck,
  FaPassport,
  FaChartLine,
  FaChevronDown,
} from "react-icons/fa";
import ApplicationListTable from "./application/ApplicationListTable";
import Message_Notification from "./application/Message_Notification";
import ShortlistingTable from "./application/ShortlistingTable";
import StatusTrackingTable from "./application/StatusTrackingTable";
import CommentsSection from "./CommentsSection";
import StudentDocs from "./StudentDocs";
import UniversityTable from "./UniversityTable";
import VisaProcess from "../student/visa_process/VisaProcess";
import { useSelector, useDispatch } from "react-redux";
import AdminComments from "../common/AdminComments";
import { MarkSideBarChatNotifications } from "../../services/user.services";
import { getChatNotifications } from "../../services/admin.services";
import { setActiveApplicationTab } from "../../state/reducers/applicationSlice";
import StaticForm from "../student/student_appication_process/StaticForm";
import ShortlistingPage from "./ShortlistingPage";
import FinalizedUniversityTab from "./FinalizedUniversityTab";
import EditableNotes from "./EditableNotes";
import { useLocation } from "react-router-dom";

const StudentApplicationList = ({ fetchStudentDetails, tabsWidth }) => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([]);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const location = useLocation();
  // Use the activeApplicationTab from Redux instead of local state
  const activeTab = useSelector(
    (state) => state.application.activeApplicationTab || "messages"
  );
  const selectedStudent = useSelector((state) => state.app.selectedStudent);
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);

  const [documentCount, setDocumentnCount] = useState(30);
  const [messageCount, setMessageCount] = useState(10);
  const [shortlistingCount, setShortlistingCount] = useState(0);
  const [finalizedUniversity, setFinalizedUniversity] = useState(0);
  const [visaCount, setVisaCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const finalizedCommentsRef = useRef(null);

  // Modified to dispatch tab changes to Redux instead of local state
  const setActiveTab = (tabId) => {
    dispatch(setActiveApplicationTab(tabId));
    setShowMobileDropdown(false); // Close dropdown when tab is selected
  };

  const markNotificationsAsRead = async (title) => {
    try {
      const response = await MarkSideBarChatNotifications();
      if (response) {
        const res = await getChatNotifications();
        setMessages(res.data);
      }
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          markNotificationsAsRead("university_application_process");
        }
      },
      { threshold: 0.3 }
    );

    if (finalizedCommentsRef.current) {
      observer.observe(finalizedCommentsRef.current);
    }

    return () => {
      if (finalizedCommentsRef.current) {
        observer.unobserve(finalizedCommentsRef.current);
      }
    };
  }, [finalizedCommentsRef]);

  // Get notifications and update message counts
  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      try {
        const res = await getChatNotifications();
        // ✅ FIX: Access the notifications array from the response
        const notifications = res.data?.notifications || [];
        setMessages(notifications);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const getTotalUnreadMessages = () => {
    return messages.filter((msg) => !msg.is_read).length;
  };

  useEffect(() => {
    // Update unread counts based on messages
    const shortlistingUnread = messages.filter(
      (msg) => msg.query_on === "shortlisting_of_universities" && !msg.is_read
    ).length;

    const finalizedUnread = messages.filter(
      (msg) => msg.query_on === "university_application_process" && !msg.is_read
    ).length;

    const visaUnread = messages.filter(
      (msg) => msg.query_on === "visa_process" && !msg.is_read
    ).length;

    setShortlistingCount(shortlistingUnread);
    setFinalizedUniversity(finalizedUnread);
    setVisaCount(visaUnread);
  }, [messages]);

  const renderContent = () => {
    return (
      <AdminComments
        activeTab={{
          name: "University Application Process",
          title: "university_application_process",
          count: 3,
        }}
      />
    );
  };

  // Tab definitions
  const tabs = [
    {
      id: "messages",
      label: "Messages & Notifications",
      shortLabel: "Messages", // Shorter label for mobile
      icon: <FaComment className="mr-1 sm:mr-2" />,
      count: getTotalUnreadMessages(),
      component: (
        <CommentsSection
          messages={messages}
          setMessages={setMessages}
          queryOnFromState={location.state?.queryOn}
        />
      ),
      always: true,
    },
    {
      id: "documents",
      label: "Documents",
      shortLabel: "Documents",
      icon: <FaFile className="mr-1 sm:mr-2" />,
      component: <StudentDocs />,
      count: null,
      always: true,
    },
    {
      id: "shortlisting",
      label: "Shortlisting",
      shortLabel: "Shortlisting",
      icon: <FaUniversity className="mr-1 sm:mr-2" />,
      count: shortlistingCount,
      component: (
        <ShortlistingPage
          fetchStudentDetails={fetchStudentDetails}
          setMessages={setMessages}
        />
      ),
      always: true,
    },
    {
      id: "finalizedUniversity",
      label: "Finalized Universities",
      shortLabel: "Finalized",
      icon: <FaCheck className="mr-1 sm:mr-2" />,
      count: finalizedUniversity,
      component: (
        <FinalizedUniversityTab
          fetchStudentDetails={fetchStudentDetails}
          selectedStudent={selectedStudent}
          finalizedCommentsRef={finalizedCommentsRef}
          renderContent={renderContent}
        />
      ),
      condition: selectedStudent?.is_finalised,
    },
    {
      id: "visa",
      label: "Visa Process",
      shortLabel: "Visa",
      icon: <FaPassport className="mr-1 sm:mr-2" />,
      count: visaCount,
      component: <VisaProcess setMessages={setMessages} />,
      always: true,
    },
    {
      id: "status",
      label: "Status & Tracking",
      shortLabel: "Status",
      icon: <FaChartLine className="mr-1 sm:mr-2" />,
      count: null,
      component: <StatusTrackingTable />,
      always: true,
    },
  ];

  // Filter visible tabs
  const visibleTabs = tabs.filter(
    (tab) => (tab.always || tab.condition) && tab.condition !== false
  );

  // Get current active tab info
  const currentTab =
    visibleTabs.find((tab) => tab.id === activeTab) || visibleTabs[0];

  return (
    <div className="p-2 sm:p-4 min-h-screen">
      {/* Mobile Dropdown Navigation (visible on small screens) */}
      <div className="block sm:hidden mb-4">
        <div className="relative">
          <button
            onClick={() => setShowMobileDropdown(!showMobileDropdown)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm"
          >
            <div className="flex items-center">
              {currentTab?.icon}
              <span className="font-medium text-gray-700">
                {currentTab?.shortLabel}
              </span>
              {currentTab?.count > 0 && (
                <span className="ml-2 bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {currentTab.count}
                </span>
              )}
            </div>
            <FaChevronDown
              className={`transition-transform duration-200 ${
                showMobileDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Mobile Dropdown Menu */}
          {showMobileDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    w-full flex items-center px-4 py-3 text-left border-b border-gray-100 last:border-b-0
                    ${
                      activeTab === tab.id
                        ? "bg-purple-50 text-purple-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }
                  `}
                >
                  {tab.icon}
                  <span className="font-medium">{tab.shortLabel}</span>
                  {tab.count > 0 && (
                    <span className="ml-auto bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Desktop Tabs Navigation (hidden on small screens) */}
      <div
        className="hidden sm:block border-b border-gray-200 mb-6"
        style={{ width: tabsWidth }}
      >
        <div className="flex overflow-x-auto">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center whitespace-nowrap px-3 md:px-6 py-4 font-medium text-xs sm:text-sm relative 
                ${
                  activeTab === tab.id
                    ? "text-purple-600"
                    : "text-gray-600 hover:text-purple-500"
                }
              `}
            >
              {tab.icon}
              <span className="truncate">{tab.label}</span>
              {tab.count > 0 && (
                <span className="ml-2 bg-red-100 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0">
                  {tab.count}
                </span>
              )}
              {/* Underline for active tab */}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-purple-600"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12 w-full">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      ) : (
        /* Tab Content */
        <div className="w-full bg-white p-2 sm:p-4 rounded-lg">
          {visibleTabs.map(
            (tab) =>
              activeTab === tab.id && (
                <div key={tab.id} className="w-full">
                  {tab.component}
                </div>
              )
          )}
        </div>
      )}

      {/* Overlay to close mobile dropdown when clicking outside */}
      {showMobileDropdown && (
        <div
          className="fixed inset-0 z-40 sm:hidden"
          onClick={() => setShowMobileDropdown(false)}
        />
      )}
    </div>
  );
};

export default StudentApplicationList;
